import { AsyncPipe } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    input,
    ChangeDetectionStrategy
} from "@angular/core";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { Observable, Subject, combineLatest } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";
import { PermittedDirective } from "~app/common/directives/permitted/permitted.directive";
import { EntityType, RaskRoller } from "~app/common/models/constants";
import { IVirkeskopKontraktEntityDTO } from "~app/common/models/entity-models";
import { ansvarigVirkeskoparePermission } from "~app/common/permissions/ansvarigVirkeskopare.permissions";
import { RolePermissionParameters, canEdit } from "~app/role-permissions";
import { AtgardListComponent } from "../../atgarder/atgard-list/atgard-list.component";
import { Kontrakt } from "../../models/kontrakt-models";
import { AvverkningskostnaderMediatorService } from "../../services/avverkningskostnader-mediator.service";
import { BetalningMediatorService } from "../../services/betalning-mediator.service";
import { ChecklistMediatorService } from "../../services/checklist-mediator.service";
import { DigitalSigneringMediatorService } from "../../services/digitalsignering-mediator.service";
import { KontraktBilagorService } from "../../services/kontrakt-bilagor.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { KontraktStatusService } from "../../services/kontrakt-status.service";
import { LeveransvirkesvolymMediatorService } from "../../services/leveransvirkesvolym-mediator.service";
import { MatbeskedMediatorService } from "../../services/matbesked-mediator.service";
import { SkogsvardBetalningMediatorService } from "../../services/skogsvard-betalning-mediator.service";
import { UtskriftMediatorService } from "../../services/utskrift-mediator.service";
import { VirkeskostnaderMediatorService } from "../../services/virkeskostnader-mediator.service";
import { SkogsvardListComponent } from "../../skogsvard/skogsvard-list/skogsvard-list.component";
import { KontraktCertifieringComponent } from "../certifiering/kontrakt-certifiering/kontrakt-certifiering.component";
import { KontraktAnteckningarComponent } from "../kontrakt-anteckningar/kontrakt-anteckningar.component";
import { KontraktBetalningComponent } from "../kontrakt-betalning/kontrakt-betalning.component";
import { KontraktBilagorComponent } from "../kontrakt-bilagor/kontrakt-bilagor.component";
import { KontraktChecklistComponent } from "../kontrakt-checklist/kontrakt-checklist.component";
import { KontraktDigitalsigneringComponent } from "../kontrakt-digitalsignering/kontrakt-digitalsignering.component";
import { KontraktForkalkylComponent } from "../kontrakt-forkalkyl/kontrakt-forkalkyl.component";
import { KontraktHeaderComponent } from "../kontrakt-header/kontrakt-header.component";
import { KontraktNvbComponent } from "../kontrakt-nvb/kontrakt-nvb.component";
import { KontraktUtskriftDokumentComponent } from "../kontrakt-utskrift-dokument/kontrakt-utskrift-dokument.component";
import { KontraktUtskriftComponent } from "../kontrakt-utskrift/kontrakt-utskrift.component";
import { LeveransvirkesplanComponent } from "../leveransvirkesplan/leveransvirkesplan.component";
@Component({
    selector: "rask-virkeskop-kontrakt",
    templateUrl: "./virkeskop-kontrakt.component.html",
    styleUrls: ["./virkeskop-kontrakt.component.scss"],
    providers: [
        AvverkningskostnaderMediatorService,
        BetalningMediatorService,
        ChecklistMediatorService,
        DigitalSigneringMediatorService,
        KontraktBilagorService,
        KontraktStatusService,
        LeveransvirkesvolymMediatorService,
        MatbeskedMediatorService,
        SkogsvardBetalningMediatorService,
        UtskriftMediatorService,
        VirkeskostnaderMediatorService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        KontraktChecklistComponent,
        KontraktHeaderComponent,
        KontraktForkalkylComponent,
        AtgardListComponent,
        SkogsvardListComponent,
        LeveransvirkesplanComponent,
        KontraktCertifieringComponent,
        KontraktBetalningComponent,
        KontraktNvbComponent,
        KontraktAnteckningarComponent,
        KontraktUtskriftComponent,
        KontraktUtskriftDokumentComponent,
        KontraktBilagorComponent,
        TogglePanelComponent,
        TabsComponent,
        TabComponent,
        KontraktDigitalsigneringComponent,
        AsyncPipe,
        PermittedDirective
    ]
})
export class VirkeskopKontraktComponent implements OnInit, OnDestroy {
    readonly entity = input.required<IVirkeskopKontraktEntityDTO>();

    protected readonly digitalSigneringPermissions: RolePermissionParameters[] = [
        {
            role: RaskRoller.role.Virkeskopa,
            canView: ansvarigVirkeskoparePermission,
            canEdit: ansvarigVirkeskoparePermission
        },
        {
            role: RaskRoller.role.Kopleda,
            canView: true,
            canEdit: true
        }
    ];

    protected kontrakt: Kontrakt;
    protected kontraktReadonly = false;
    protected enableSignSkogsvardTab$: Observable<boolean>;
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly canEdit = canEdit();

    ngOnInit(): void {
        this.enableSignSkogsvardTab$ = combineLatest([
            this.kontraktMediatorService.kontrakt$,
            this.kontraktMediatorService.atgarderPreload$
        ]).pipe(
            map(([kontrakt, atgarder]) => kontrakt.showSkogsvard && atgarder.some(x => x.type === EntityType.virkeskopsAtgardSkogsvard))
        );

        this.kontraktMediatorService.kontrakt$.pipe(
            filter(k => k !== null),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(k => this.kontrakt = k);

        this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit()),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(readonly => this.kontraktReadonly = readonly);
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }
}
